import { useEffect, useRef, useState } from "react";
import {
  IconArrowDropDown,
  IconArrowDropUp,
  Input,
  MENU_PLACEMENT,
  MenuListItem,
} from "@cdkglobal/radial";
import { useIntl } from "react-intl";
import { uniqueId } from "underscore";

import { useUserContext } from "../../../../contexts/UserContext";
import {
  getCompanyIdByStoreId,
  getStoreOptionByGroupId,
  dispatchUpdateField,
} from "../../helpers";
import { customEvaluations, processDependenyRules } from "../../evaluations";
import { StyledInlineInput, StyledMenu } from "../../../../utils/styled";
import { ISelectableOption, RenderDropdownInputProps } from "../../../../types";

export default function RenderDropdownInput({
  title,
  propertyKey,
  rowIndex,
  propertySubKey,
  selectedOption,
  optionsList,
  companyKey,
  storeKey,
  dependentFields,
  evalMethod,
  isRequired,
  isDisabled,
  error,
  message,
  dependencyRules,
  triggerDependencyRule,
  isDialogField,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
  isGroupId,
  isInline = false,
}: Readonly<RenderDropdownInputProps>): JSX.Element {
  const intl = useIntl();
  const { iamUserState, evaluatedResults } = useUserContext();

  const targetRef = useRef<HTMLInputElement>(null);
  const dimensionRef = useRef<HTMLLabelElement>(null);
  const menuContainerRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [updatedOptionsList, setUpdatedOptionsList] =
    useState<ISelectableOption[]>(optionsList);

  const menuPlacement =
    targetRef.current &&
    window.innerHeight - targetRef.current.getBoundingClientRect().bottom < 200
      ? MENU_PLACEMENT.TOP_START
      : MENU_PLACEMENT.BOTTOM_START;

  const id =
    rowIndex !== undefined && propertySubKey
      ? `-${rowIndex}-${propertySubKey}`
      : "";

  const formattedId = `${propertyKey}${id}${
    isInline ? "-inline-dropdown" : "-dropdown"
  }`;

  const InputComponent = isInline ? StyledInlineInput : Input;

  const handleOpen = () => {
    if (!isDisabled) setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleClick = () => {
    if (!isDisabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const updateStoreField = (
    option: ISelectableOption,
    isForResetField = false,
  ) => {
    if (!storeKey) {
      return;
    }

    const storeIdOption = getStoreOptionByGroupId({
      selectedGroupId: option.value as string,
      iamUserState,
    });

    dispatchUpdateField({
      propertyKey,
      propertySubKey,
      rowIndex,
      newFieldValue: storeIdOption.value,
      isDialogField,
      selectedTabSectionKey,
      targetFieldKey: storeKey,
      isForResetField,
      dispatch,
    });
  };

  const updateCompanyField = (option: ISelectableOption) => {
    if (!companyKey) {
      return;
    }

    const storeId = isGroupId
      ? getStoreOptionByGroupId({
          selectedGroupId: option.value as string,
          iamUserState,
        }).value
      : (option.value as string);

    const companyIdValue = getCompanyIdByStoreId(
      storeId,
      iamUserState.extendedAccountInfo,
    );

    dispatchUpdateField({
      propertyKey,
      propertySubKey,
      rowIndex,
      newFieldValue: companyIdValue,
      isDialogField,
      selectedTabSectionKey,
      targetFieldKey: companyKey,
      dispatch,
    });
  };

  const clearDependentFields = () => {
    dependentFields?.forEach((targetKey) => {
      dispatchUpdateField({
        propertyKey,
        propertySubKey,
        rowIndex,
        newFieldValue: "",
        isDialogField,
        selectedTabSectionKey,
        targetFieldKey: targetKey,
        dispatch,
      });
    });
  };

  const handleSelect = (option: ISelectableOption) => {
    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey,
        newFieldValue: option.value,
        rowIndex,
        propertySubKey,
        isDialogField,
        selectedTabSectionKey,
      },
    });

    clearDependentFields();
    setIsOpen(false);
  };

  const isOptionSelected = (option: ISelectableOption): boolean =>
    selectedOption.label === option.label ||
    (selectedOption.value !== "" && selectedOption.value === option.value);

  const handleSelectionDependency = (option: ISelectableOption) => {
    updateCompanyField(option);
    updateStoreField(option);
    updateStoreField(option, true); // keep track of reset state

    if (evalMethod) {
      customEvaluations({
        evalMethod,
        propertyKey,
        propertySubKey,
        rowIndex,
        inputValue: option.value,
        searchResponse: undefined,
        evaluatedResults,
        rowDataReducerState,
        selectedTabSectionKey,
        dispatch,
      });
    }
  };

  useEffect(() => {
    setUpdatedOptionsList(optionsList);
  }, [optionsList]);

  useEffect(() => {
    if (dependencyRules && !isDialogField) {
      processDependenyRules({
        dependencyRules,
        propertyKey,
        inputValue: selectedOption.value as string | null,
        propertySubKey,
        rowIndex,
        selectedTabSectionKey,
        rowDataReducerState,
        dispatch,
      });
    }
  }, [selectedOption.value, triggerDependencyRule]);

  useEffect(() => {
    handleSelectionDependency(selectedOption);
  }, [selectedOption.value]);

  return (
    <>
      <div data-testid={formattedId}>
        <InputComponent
          dataTestId={formattedId}
          id={uniqueId(formattedId)}
          label={title}
          labelRef={dimensionRef}
          name={formattedId}
          value={selectedOption.label}
          onChange={() => {
            // Do nothing
          }}
          onClick={isDisabled ? undefined : handleClick}
          placeholder={
            isDisabled
              ? title
              : intl.formatMessage(
                  {
                    id: "placeholder.select",
                    defaultMessage: "Select {item}",
                  },
                  {
                    item: title,
                  },
                )
          }
          icon={
            !isDisabled ? (
              isOpen ? (
                <IconArrowDropUp />
              ) : (
                <IconArrowDropDown />
              )
            ) : undefined
          }
          ref={targetRef}
          isRequired={isRequired && !isDisabled}
          isDisabled={isDisabled}
          hasError={error}
          errorMessage={message}
          enableCustomValidation
          style={{ caretColor: "transparent" }}
          {...(isInline && { hideLabel: true })}
        />
      </div>

      {!isDisabled && (
        <StyledMenu
          id={`${propertyKey}-menu-container`}
          dimensionRef={dimensionRef}
          isOpen={isOpen}
          labelRef={targetRef}
          onClose={handleClose}
          onOpen={handleOpen}
          onClick={handleClick}
          isAuto={false}
          placement={menuPlacement}
          ref={menuContainerRef}
          style={
            isInline
              ? {}
              : {
                  width: dimensionRef?.current?.offsetWidth,
                  maxWidth: dimensionRef?.current?.offsetWidth,
                }
          }
        >
          {updatedOptionsList.map((option, index) => (
            <MenuListItem
              title={option.label}
              id={`${propertyKey}-menu-item-${option.label}`}
              dataTestId={`menu-list-item-${index}`}
              isSelected={isOptionSelected(option)}
              key={
                option.value === null
                  ? uniqueId("null")
                  : uniqueId(JSON.stringify(option.value))
              }
              onClick={() => handleSelect(option)}
              hideSelectedItemCheckmark={false}
              isDisabled={option.disabled}
            >
              {option.label}
            </MenuListItem>
          ))}
        </StyledMenu>
      )}
    </>
  );
}
